import { DateTime } from 'luxon';
import { makeAutoObservable } from 'mobx';
import { RechargeValueApiResponse, TransactionApiResponse, TransactionMatchApiResponse, TransactionSource } from '../../api/CalculationApi';
import { RuleApi } from '../../api/RuleApi';
import { SourceType } from '../../api/SourceApi';
import { CurrencyCode } from '../../config/CurrencyCode';
import { Rule } from './Rule';
import { LoanTransactionApiResponse } from '../../api/Loans';
import { RuleTransactionType, TxType } from '../../config/Constants';

export class RechargeValue {
  entityId: number = 0;
  value: number = 0.0;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: RechargeValueApiResponse): RechargeValue {
    const value = new RechargeValue();
    value.entityId = apiData.organisationId;
    value.value = apiData.value;

    return value;
  }
}

export class Transaction {
  id: number = 0;
  organisationId: number = 0;
  externalId: string = '';
  journalSourceId: string = '';
  date: DateTime = DateTime.now();
  created: DateTime = DateTime.now();
  amount: number = 0;
  currency: CurrencyCode = CurrencyCode.GBP;
  nominalCode: string = '';
  trackingCategory: Map<string, string> = new Map();
  contact: string = '';
  contactLink: string = '';
  title: string = '';
  description: string = '';
  type: TxType;
  sourceType: TransactionSource;
  sourceId: string;
  source: SourceType;

  // Match data
  matchId: number = 0;
  detectedRuleApportionmentId: number = 0;
  overrideRuleApportionmentId: number = 0;
  doNotRecharge: boolean = false;
  rechargeValues: RechargeValue[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: TransactionApiResponse): Transaction {
    const transaction = new Transaction();

    if (apiData) {
      transaction.id = apiData.id;
      transaction.externalId = apiData.externalId;
      transaction.organisationId = apiData.organisationId;
      transaction.journalSourceId = apiData.journalSourceId;
      transaction.date = DateTime.fromISO(apiData.date);
      transaction.created = DateTime.fromISO(apiData.created);
      transaction.amount = apiData.amount;
      transaction.currency = apiData.currency as CurrencyCode;
      transaction.nominalCode = apiData.nominalCode;
      transaction.trackingCategory = apiData.trackingCategory 
        ? new Map(apiData.trackingCategory.split(',').map(cat => [cat.split(':')[0], cat]))
        : new Map();
      transaction.contact = apiData.contact;
      transaction.contactLink = apiData.contactLink;
      transaction.title = apiData.title;
      transaction.description = apiData.description;
      transaction.type = apiData.type as TxType;
      transaction.sourceType = apiData.sourceType as TransactionSource;
      transaction.source = apiData.source as SourceType;
    }

    return transaction;
  }

  static fromMatchApi(match: TransactionMatchApiResponse): Transaction {
    const transaction = Transaction.fromApi(match.transaction);
    transaction.organisationId = match.organisationId;

    transaction.matchId = match.id;
    if (match.detectedRuleApportionmentId || match.overrideRuleApportionmentId) {
      if (match.detectedRuleApportionmentId) transaction.detectedRuleApportionmentId = match.detectedRuleApportionmentId;
      if (match.overrideRuleApportionmentId) transaction.overrideRuleApportionmentId = match.overrideRuleApportionmentId;
      if (match.rechargeValues) transaction.rechargeValues = match.rechargeValues.map(value => RechargeValue.fromApi(value));
      transaction.doNotRecharge = match.doNotRecharge;
    }

    return transaction;
  }

  static fromLoansApi(apiData: LoanTransactionApiResponse): Transaction {
    const transaction = new Transaction();

    transaction.id = apiData.transactionId;
    transaction.amount = apiData.amount;
    transaction.currency = apiData.currency as CurrencyCode;
    transaction.date = DateTime.fromISO(apiData.date);
    transaction.description = apiData.description;
    transaction.journalSourceId = apiData.journalSourceId;
    transaction.sourceType = apiData.sourceType as TransactionSource;
    transaction.title = apiData.title;

    return transaction;
  }

  isAssignedRule = (rule: Rule): boolean => (
    this.doNotRecharge === false &&
    (
      (!this.overrideRuleApportionmentId && this.detectedRuleApportionmentId === rule.ruleApportionmentId) ||
      (this.overrideRuleApportionmentId === rule.ruleApportionmentId)
    )
  );

  isUnmatched = (): boolean => this.doNotRecharge || (this.detectedRuleApportionmentId === 0 && this.overrideRuleApportionmentId === 0);

  get assignedRule(): number {
    return (!this.overrideRuleApportionmentId ? this.detectedRuleApportionmentId : this.overrideRuleApportionmentId);
  }

  get totalRecharge() {
    return this.rechargeValues
      .map(recharge => recharge.value)
      .reduce((a, b) => a + b, 0);
  }

  get ruleTransactionType(): RuleTransactionType {
    switch (this.type) {
      case TxType.EXPENSE:
        return RuleTransactionType.expense;
      case TxType.INCOME:
        return RuleTransactionType.income;
      case TxType.ASSET:
      case TxType.LIABILITY:
        return RuleTransactionType.balancesheet;
      default:
        return RuleTransactionType.none;
    }
  }

  setOverrideRule = (groupId: number, calculationId: number, ruleId: number): Promise<Transaction> => {
    return RuleApi.updateMatch(groupId, calculationId, this.matchId, ruleId, false).then(() => this);
  };

  setDoNotRecharge = (groupId: number, calculationId: number): Promise<Transaction> => {
    return RuleApi.updateMatch(groupId, calculationId, this.matchId, null, true).then(() => {
      this.doNotRecharge = true;
      return this;
    });
  };

  apportionmentForEntity = (entityId: number): number => {
    const recharge = this.rechargeValues.find(r => r.entityId === entityId);
    return recharge ? recharge.value : 0.0;
  };

  deeplinkUrl = (shortcode: string): string => {
    let url = 'https://go.xero.com/organisationlogin/default.aspx?shortcode=' + shortcode;

    switch (this.sourceType) {
      case TransactionSource.ACCREC:
      case TransactionSource.ACCRECCREDIT:
        url += `&redirecturl=/AccountsReceivable/View.aspx%3FInvoiceID=${this.journalSourceId}`;
        break;

      case TransactionSource.ACCPAY:
      case TransactionSource.ACCPAYCREDIT:
      case TransactionSource.ARCREDITPAYMENT:
      case TransactionSource.APCREDITPAYMENT:
        url += `&redirecturl=/AccountsPayable/View.aspx%3FInvoiceID=${this.journalSourceId}`;
        break;

      case TransactionSource.ACCRECPAYMENT:
      case TransactionSource.ACCPAYPAYMENT:
      case TransactionSource.CASHREC:
      case TransactionSource.CASHPAID:
        url += `&redirecturl=/Bank/ViewTransaction.aspx%3FbankTransactionID=${this.journalSourceId}`;
        break;

      case TransactionSource.MANJOURNAL:
        url += `&redirecturl=/Journal/View.aspx?invoiceID=${this.journalSourceId}`;
        break;

      case TransactionSource.SOURCE_LESS:
        url += `&redirecturl=/GeneralLedger/JournalDetails.aspx%3FjournalID=${this.journalSourceId}`;
        break;

      case TransactionSource.ARPREPAYMENT:
      case TransactionSource.APPREPAYMENT:        
      case TransactionSource.TRANSFER:
      case TransactionSource.AROVERPAYMENT:
      case TransactionSource.APOVERPAYMENT:
      case TransactionSource.EXPCLAIM:
      case TransactionSource.EXPPAYMENT:
      case TransactionSource.PAYSLIP:
      case TransactionSource.WAGEPAYABLE:
      case TransactionSource.INTEGRATEDPAYROLLPE:
      case TransactionSource.INTEGRATEDPAYROLLPT:
      case TransactionSource.EXTERNALSPENDMONEY:
      case TransactionSource.INTEGRATEDPAYROLLPTPAYMENT:
      case TransactionSource.INTEGRATEDPAYROLLCN:
      default:
        return null;
    }

    return url;
  };

  matchesSearch = (value: string): boolean => {
    return (this.description && this.description.toLowerCase().includes(value))
      || (this.title && this.title.toLowerCase().includes(value));
  };

  isNew = (lockDate: DateTime) => {
    return lockDate?.isValid && this.sourceType !== TransactionSource.RECHARGE && this.ruleTransactionType !== RuleTransactionType.none && this.created > lockDate;
  };
}
